import { Gender } from '../../utils/enumAvatar'

import { SelectedOptions, defaultOptions, genderSpecificOptions } from './types'

/**
 * Gets a random value from the specified option array
 * @template T - The key type of defaultOptions
 * @param key - The option key to get a random value for
 * @returns A random value from the options array
 */
export const getRandomValue = <T extends keyof typeof defaultOptions>(
  key: T
): (typeof defaultOptions)[T][number] => {
  const options = defaultOptions[key]
  return options[Math.floor(Math.random() * options.length)]
}

/**
 * Gets a random option value specific to the given gender
 * @param key - The option key to get a random value for
 * @param gender - The gender to get gender-specific options for
 * @returns A random value from the gender-specific options or default options
 */
export const getGenderSpecificRandomOption = (
  key: keyof SelectedOptions,
  gender: Gender
) => {
  if (key === 'topType' || key === 'clotheType') {
    try {
      // Handle both string and number gender values
      let genderStr: string
      if (typeof gender === 'string') {
        // If gender is already a string (like 'Girl'), use it directly
        genderStr = gender
      } else if (typeof gender === 'number') {
        // If gender is a number (enum value), convert to string
        genderStr = Gender[gender]
      } else {
        console.error('Invalid gender parameter:', gender)
        return getRandomValue(key as keyof typeof defaultOptions)
      }
      // Validate that genderStr exists and is valid
      if (!genderStr || typeof genderStr !== 'string') {
        console.error('Invalid gender string:', genderStr)
        return getRandomValue(key as keyof typeof defaultOptions)
      }

      // Check if genderSpecificOptions exists and has the gender key
      if (!genderSpecificOptions || typeof genderSpecificOptions !== 'object') {
        console.error('genderSpecificOptions is not available')
        return getRandomValue(key as keyof typeof defaultOptions)
      }

      const genderOptions =
        genderSpecificOptions[genderStr as keyof typeof genderSpecificOptions]
      if (!genderOptions) {
        console.error(`No options found for gender: ${genderStr}`)
        return getRandomValue(key as keyof typeof defaultOptions)
      }

      const options = genderOptions[key]
      if (!options || !Array.isArray(options) || options.length === 0) {
        console.error(`No ${key} options found for gender: ${genderStr}`)
        return getRandomValue(key as keyof typeof defaultOptions)
      }

      const randomOption = options[Math.floor(Math.random() * options.length)]
      return randomOption
    } catch (error) {
      console.error('Error in getGenderSpecificRandomOption:', error)
      return getRandomValue(key as keyof typeof defaultOptions)
    }
  }
  return getRandomValue(key as keyof typeof defaultOptions)
}

/**
 * Gets initial avatar options based on the given gender
 * @param gender - The gender to get initial options for
 * @returns Initial SelectedOptions object with gender-appropriate defaults
 */
export const getInitialOptions = (gender: Gender): SelectedOptions => {
  // Handle both string and number gender values
  let normalizedGender: Gender
  if (typeof gender === 'string') {
    // Convert string gender to enum value
    switch (gender) {
      case 'Boy':
        normalizedGender = Gender.Boy
        break
      case 'Girl':
        normalizedGender = Gender.Girl
        break
      case 'Other':
        normalizedGender = Gender.Other
        break
      default:
        console.error('Unknown gender string:', gender)
        normalizedGender = Gender.Boy // Default fallback
    }
  } else {
    normalizedGender = gender
  }

  return {
    topType: (() => {
      switch (normalizedGender) {
        case Gender.Boy:
          return 'ShortHairShortFlat'
        case Gender.Girl:
          return 'LongHairBob'
        default:
          return 'NoHair'
      }
    })(),
    hairColor: 'Brown',
    accessoriesType: 'Blank',
    facialHairType: 'Blank',
    clotheType: (() => {
      switch (normalizedGender) {
        case Gender.Boy:
          return 'ShirtCrewNeck'
        case Gender.Girl:
          return 'Overall'
        default:
          return 'BlazerShirt'
      }
    })(),
    clotheColor: 'Blue01',
    eyeType: 'Default',
    eyebrowType: 'Default',
    mouthType: 'Smile',
    skinColor: 'Light',
    avatarStyle: 'Circle',
    gender: normalizedGender
  }
}

/**
 * Validates if an option value is valid for gender-specific options
 * @param option - The option key to validate
 * @param value - The value to validate
 * @param gender - The gender context for validation
 * @returns True if the option is valid for the given gender
 */
export const isValidGenderSpecificOption = (
  option: keyof SelectedOptions,
  value: string,
  gender: Gender
): boolean => {
  if (option === 'topType' || option === 'clotheType') {
    try {
      // Handle both string and number gender values (same logic as getGenderSpecificRandomOption)
      let genderStr: string
      if (typeof gender === 'string') {
        genderStr = gender
      } else if (typeof gender === 'number') {
        genderStr = Gender[gender]
      } else {
        console.error('Invalid gender parameter in validation:', gender)
        return true // Allow the option if we can't validate
      }

      if (
        !genderStr ||
        !genderSpecificOptions[genderStr as keyof typeof genderSpecificOptions]
      ) {
        console.error('Invalid gender string in validation:', genderStr)
        return true // Allow the option if we can't validate
      }

      const genderOptions =
        genderSpecificOptions[genderStr as keyof typeof genderSpecificOptions][
          option
        ]
      return genderOptions.includes(value)
    } catch (error) {
      console.error('Error in gender-specific validation:', error)
      return true // Allow the option if validation fails
    }
  }
  return true
}

/**
 * Compares two objects of the same type for equality by comparing all their properties
 * More efficient than JSON.stringify comparison and type-safe

 */
export const areObjectsEqual = <T extends object>(a: T, b: T): boolean => {
  const keysA = Object.keys(a) as Array<keyof T>
  const keysB = Object.keys(b) as Array<keyof T>

  // Check if both objects have the same number of properties
  if (keysA.length !== keysB.length) {
    return false
  }

  // Check if all properties are equal
  return keysA.every((key) => a[key] === b[key])
}
